import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { meApi } from "@/lib/api";
import { PageHeader, Card } from "./_shared";
import { toast } from "sonner";
import { CalendarCheck, MapPin, FileText, Clock, CheckCircle2, XCircle, AlarmClock, CalendarPlus, Plus, X, Loader2 } from "lucide-react";
import CalendarAddButton from "@/components/CalendarAddButton";
import { formatDateTimeLong } from "@/lib/date";

const STATUS_META = {
  planifie: { label: "Planifié", color: "text-[#DCB457]", border: "border-[#DCB457]/40" },
  en_attente: { label: "En attente", color: "text-amber-300", border: "border-amber-400/40" },
  termine: { label: "Terminé", color: "text-emerald-300", border: "border-emerald-400/40" },
  annule: { label: "Annulé", color: "text-red-300", border: "border-red-400/40" },
};

const MOTIFS = [
  "Rendez-vous découverte",
  "Visite du lieu / repérage",
  "Préparation playlist",
  "Finalisation du programme",
  "Signature du contrat",
  "Point avancement",
  "Autre demande",
];

function fmtLong(iso) {
  return formatDateTimeLong(iso) || (iso ? "—" : "—");
}
function fmtShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtDateOnly(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
}

// Countdown → returns { days, hours, minutes, isPast }
function useCountdown(iso) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    const diff = t - Date.now();
    const isPast = diff < 0;
    const abs = Math.abs(diff);
    const days = Math.floor(abs / 86_400_000);
    const hours = Math.floor((abs % 86_400_000) / 3_600_000);
    const minutes = Math.floor((abs % 3_600_000) / 60_000);
    return { days, hours, minutes, isPast, tick };
  }, [iso, tick]);
}

function RdvCard({ r, highlighted = false }) {
  const cd = useCountdown(r.date);
  const status = STATUS_META[r.status] || STATUS_META.planifie;
  const isPast = cd?.isPast;
  const durationMin = r.duration_minutes || 60;

  return (
    <div
      className={`p-5 md:p-6 border transition ${highlighted ? "border-[#DCB457] bg-[#DCB457]/[0.05]" : "border-[#DCB457]/15"}`}
      data-testid={`rdv-card-${r.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] border ${status.border} ${status.color} px-2 py-0.5`}>
              {r.status === "termine" && <CheckCircle2 size={11} strokeWidth={1.5} />}
              {r.status === "annule" && <XCircle size={11} strokeWidth={1.5} />}
              {(!r.status || r.status === "planifie") && <AlarmClock size={11} strokeWidth={1.5} />}
              {status.label}
            </span>
            {highlighted && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-0.5 bg-[#DCB457]/8">
                Prochain RDV
              </span>
            )}
          </div>
          <p className="font-serif-luxe text-lg md:text-xl">{r.type || "Rendez-vous"}</p>
          <p className={`text-sm mt-1 ${highlighted ? "gold-text" : "text-[#DCB457]"}`}>{fmtLong(r.date)}</p>
        </div>
        {highlighted && cd && !isPast && r.status !== "annule" && (
          <div className="text-right shrink-0" data-testid={`rdv-countdown-${r.id}`}>
            <p className="eyebrow mb-1">Décompte</p>
            <p className="font-serif-luxe text-2xl md:text-3xl gold-text">
              {cd.days > 0 ? `${cd.days}j ${cd.hours}h` : cd.hours > 0 ? `${cd.hours}h ${cd.minutes}min` : `${cd.minutes} min`}
            </p>
          </div>
        )}
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="inline-flex items-center gap-1.5 text-white/70">
          <Clock size={13} strokeWidth={1.4} className="text-[#DCB457]/70 shrink-0" />
          <span>Durée : {durationMin} min</span>
        </div>
        {r.lieu && (
          <div className="inline-flex items-center gap-1.5 text-white/70">
            <MapPin size={13} strokeWidth={1.4} className="text-[#DCB457]/70 shrink-0" />
            <span className="truncate">{r.lieu}</span>
          </div>
        )}
      </div>
      {r.notes && (
        <div className="mt-3 pt-3 border-t border-[#DCB457]/10 flex items-start gap-1.5 text-sm text-white/60">
          <FileText size={13} strokeWidth={1.4} className="text-[#DCB457]/60 mt-0.5 shrink-0" />
          <p className="leading-relaxed">{r.notes}</p>
        </div>
      )}
      {!isPast && r.status !== "annule" && (
        <div className="mt-4 pt-3 border-t border-[#DCB457]/10">
          <CalendarAddButton
            title={r.motif || "Rendez-vous My Animation"}
            description={r.notes || ""}
            location={r.lieu || ""}
            startISO={r.date}
            durationMin={durationMin}
            icsHref={meApi.rdvIcsUrl(r.id)}
            filenameHint={`rdv-${(r.date || "").slice(0, 10)}`}
            testIdPrefix={`rdv-cal-${r.id}`}
          />
        </div>
      )}
    </div>
  );
}

export default function RDV() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");
  const [showRequestModal, setShowRequestModal] = useState(false);

  const reload = () => meApi.rdv().then((data) => setItems(Array.isArray(data) ? data : [])).catch(() => {});
  useEffect(() => {
    reload().finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const upcoming = items.filter((r) => r.status !== "annule" && new Date(r.date).getTime() >= now).sort((a, b) => new Date(a.date) - new Date(b.date));
  const past = items.filter((r) => r.status === "termine" || (r.status !== "annule" && new Date(r.date).getTime() < now)).sort((a, b) => new Date(b.date) - new Date(a.date));
  const cancelled = items.filter((r) => r.status === "annule").sort((a, b) => new Date(b.date) - new Date(a.date));
  const next = upcoming[0] || null;
  const others = upcoming.slice(1);

  const listByTab = tab === "past" ? past : tab === "cancelled" ? cancelled : upcoming;

  return (
    <div data-testid="ec-rdv">
      <PageHeader
        eyebrow="Agenda"
        title="Mes"
        italic="rendez-vous."
        desc="Rendez-vous de préparation, repérage et jour J planifiés par votre équipe. Un rappel automatique vous est envoyé 24 h avant chaque RDV."
      />

      {/* Request-a-new-RDV button */}
      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={() => setShowRequestModal(true)}
          data-testid="rdv-request-open-btn"
          className="btn-gold inline-flex items-center gap-2 text-[11px]"
        >
          <Plus size={14} strokeWidth={1.6} />
          Demander un RDV
        </button>
      </div>

      {showRequestModal && (
        <RdvRequestModal
          onClose={() => setShowRequestModal(false)}
          onSuccess={() => { setShowRequestModal(false); reload(); }}
        />
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="rdv-kpi">
        <div className="card-luxe p-5 text-center">
          <p className="eyebrow mb-2">À venir</p>
          <p className="font-serif-luxe text-3xl gold-text">{upcoming.length}</p>
        </div>
        <div className="card-luxe p-5 text-center">
          <p className="eyebrow mb-2">Passés</p>
          <p className="font-serif-luxe text-3xl">{past.length}</p>
        </div>
        <div className="card-luxe p-5 text-center">
          <p className="eyebrow mb-2">Annulés</p>
          <p className="font-serif-luxe text-3xl text-red-300/85">{cancelled.length}</p>
        </div>
        <div className="card-luxe p-5 text-center">
          <p className="eyebrow mb-2">Prochain RDV</p>
          <p className="font-serif-luxe text-lg gold-text">{next ? fmtShort(next.date) : "—"}</p>
        </div>
      </div>

      {loading ? (
        <Card><p className="text-sm text-white/55 italic">Chargement de vos rendez-vous…</p></Card>
      ) : items.length === 0 ? (
        <Card>
          <div className="text-center py-10">
            <CalendarCheck size={30} strokeWidth={1} className="text-[#DCB457]/60 mx-auto mb-3" />
            <p className="text-white/65 text-sm">Aucun rendez-vous planifié pour l&apos;instant.</p>
            <p className="text-white/40 text-xs mt-2">Votre DJ vous proposera prochainement un créneau de préparation.</p>
          </div>
        </Card>
      ) : (
        <>
          {/* Highlight: next RDV */}
          {next && (
            <section className="mb-8" data-testid="rdv-next-section">
              <RdvCard r={next} highlighted />
            </section>
          )}

          {/* Tabs */}
          <div className="flex items-center gap-2 border-b border-[#DCB457]/15 mb-6 overflow-x-auto" data-testid="rdv-tabs">
            {[
              ["upcoming", `À venir (${others.length + (next ? 1 : 0)})`],
              ["past", `Historique (${past.length})`],
              ["cancelled", `Annulés (${cancelled.length})`],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                data-testid={`rdv-tab-${k}`}
                className={`px-4 py-3 text-[11px] uppercase tracking-[0.22em] border-b-2 -mb-px transition ${
                  tab === k ? "border-[#DCB457] text-[#DCB457]" : "border-transparent text-white/55 hover:text-white/80"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* List */}
          <section className="space-y-4" data-testid={`rdv-list-${tab}`}>
            {(tab === "upcoming" ? others : listByTab).length === 0 ? (
              <p className="text-sm text-white/45 italic text-center py-6">Aucun rendez-vous {tab === "past" ? "passé" : tab === "cancelled" ? "annulé" : "à venir supplémentaire"}.</p>
            ) : (
              (tab === "upcoming" ? others : listByTab).map((r) => <RdvCard key={r.id} r={r} />)
            )}
          </section>

          <p className="text-[11px] text-white/40 text-center mt-8" data-testid="rdv-reminder-note">
            🔔 Un rappel automatique vous est envoyé par email 24 h avant chaque rendez-vous.<br />
            En cas de modification, vous êtes notifié immédiatement.
          </p>
        </>
      )}
    </div>
  );
}

// ==========================================================================
// Client "Request a RDV" modal — sent to admin for validation
// ==========================================================================
function RdvRequestModal({ onClose, onSuccess }) {
  const [motif, setMotif] = useState(MOTIFS[0]);
  const [motifOther, setMotifOther] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("14:00");
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Default date = tomorrow
  useEffect(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    setDate(d.toISOString().slice(0, 10));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const finalMotif = (motif === "Autre demande" ? motifOther : motif).trim();
    if (!finalMotif) { toast.error("Merci de préciser le motif du RDV."); return; }
    if (!date || !time) { toast.error("Merci de choisir une date et une heure."); return; }
    // Combine date + time (local time — user's timezone)
    const iso = new Date(`${date}T${time}:00`).toISOString();
    setBusy(true);
    try {
      await meApi.requestRdv({
        date: iso,
        duration_minutes: Number(duration) || 60,
        motif: finalMotif,
        notes: notes.trim(),
      });
      toast.success("Demande envoyée ! Vous recevrez une confirmation par email dès validation.");
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'envoyer la demande.");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="rdv-request-modal"
      style={{ isolation: "isolate" }}
    >
      <div className="absolute inset-0 bg-black/85" />
      <form
        onSubmit={submit}
        className="relative w-full max-w-lg bg-[#0a0a0a] border border-[#DCB457]/30 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 pb-4 border-b border-[#DCB457]/15 relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-white/50 hover:text-[#DCB457]"
            aria-label="Fermer"
            data-testid="rdv-request-close"
          >
            <X size={18} strokeWidth={1.4} />
          </button>
          <p className="eyebrow">Nouveau rendez-vous</p>
          <h3 className="font-serif-luxe text-2xl mt-1">
            Proposez un <span className="italic gold-text">créneau.</span>
          </h3>
          <p className="text-white/60 text-sm mt-2">
            Choisissez le motif et la date qui vous conviennent. Nous confirmons sous 24h ouvrées.
          </p>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">
              Motif du rendez-vous *
            </label>
            <select
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              required
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2.5 text-sm outline-none"
              data-testid="rdv-request-motif"
            >
              {MOTIFS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            {motif === "Autre demande" && (
              <input
                type="text"
                value={motifOther}
                onChange={(e) => setMotifOther(e.target.value)}
                placeholder="Précisez votre motif…"
                required
                className="w-full mt-2 bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none"
                data-testid="rdv-request-motif-other"
              />
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">Date souhaitée *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none"
                data-testid="rdv-request-date"
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">Heure souhaitée *</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
                step="900"
                className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none"
                data-testid="rdv-request-time"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">Durée estimée</label>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none"
              data-testid="rdv-request-duration"
            >
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>1 heure</option>
              <option value={90}>1h30</option>
              <option value={120}>2 heures</option>
            </select>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-white/55 mb-1.5">Notes / précisions</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ex : préférence pour un RDV en visio, questions à aborder, etc."
              className="w-full bg-white/[0.03] border border-white/10 focus:border-[#DCB457] px-3 py-2 text-sm outline-none resize-none"
              data-testid="rdv-request-notes"
            />
            <p className="text-[10px] text-white/40 mt-1">{notes.length}/500</p>
          </div>
        </div>

        <div className="p-6 pt-4 border-t border-[#DCB457]/15 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-[11px] uppercase tracking-[0.2em] px-4 py-2 text-white/70 hover:text-white border border-white/15"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="rdv-request-submit"
            className="btn-gold text-[11px] inline-flex items-center gap-2 disabled:opacity-40"
          >
            {busy ? (
              <><Loader2 size={12} className="animate-spin" /> Envoi…</>
            ) : (
              <><CalendarPlus size={12} strokeWidth={1.6} /> Envoyer ma demande</>
            )}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

